// @erp/module-cms/schemas — the ANNOUNCEMENTS content type wire contracts. This is
// the PATTERN-SETTING content slice: the later content types (tenders / events /
// publications / gallery) clone this Create/Update/DTO/list-query + slug/status/
// soft-delete shape. Browser-safe: zod + plain data only (fe forms and the gateway
// parse the SAME schemas, so the admin UI and the server can never disagree on what
// a valid announcement is). Feature-referenced from MBSPSU (READ-ONLY) — rebuilt for
// the target stack, English-first + locale-ready (a translation is a SIBLING ROW
// under a different `locale`, never a *_hi/*_pa column explosion — the 0002 law).
//
// ┌──────────────────────────────────────────────────────────────────────────────┐
// │ SECURITY — body_html is stored RAW and is NOT sanitized on write.             │
// │ Sanitization happens ON THE WAY OUT: every /public/* route passes bodyHtml     │
// │ through the allowlist sanitiser (api/sanitize.ts, Cloudflare HTMLRewriter —   │
// │ wired 2026-09-13 per ADR-010 §2.4) before it leaves the ERP; the external site │
// │ renders the result as-is (schemas/public.ts is the contract). The admin reads  │
// │ still carry the raw column; content is authored ONLY by trusted cms:* holders. │
// └──────────────────────────────────────────────────────────────────────────────┘
import { z } from "zod";
import { HttpUrl, SafeHref } from "./href.js";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";
import { SLUG_MAX, SLUG_RE } from "./categories.js";
import { Locale } from "./locale.js";
import { PublishingStatus } from "./settings.js";
import type { NewTagUnit } from "./settings.js";
import { Highlight, Highlights, Tags } from "./structured.js";

// NOTE: slugify + SLUG_MAX/SLUG_RE are REUSED from the categories slice (the ONE
// slug grammar + deriver, hardened there: symbol/non-latin → "" → id fallback,
// bounded) and re-exported by schemas/index.js via categories — the repo imports
// slugify from the barrel. Not re-exported here (that would double a `export *`).

// ── The content-type vocabulary (the decided kinds — a bad type 400s) ──────────
// The MBSPSU display labels (Admission | Notice | Press Release | Announcement |
// Event | Tender) rebuilt as stable lower-snake tokens (the store speaks tokens; a
// label is a render concern).
export const CMS_ANNOUNCEMENT_TYPES = [
  "admission",
  "notice",
  "press_release",
  "announcement",
  "event",
  "tender",
] as const;
export const AnnouncementType = z.enum(CMS_ANNOUNCEMENT_TYPES);
export type AnnouncementType = z.infer<typeof AnnouncementType>;

// ── The publishing status enum — REUSED from settings (draft|published|archived),
// so the content lifecycle and the site-wide default_publishing_status can never
// drift onto different value sets. ─────────────────────────────────────────────
export const AnnouncementStatus = PublishingStatus;
export type AnnouncementStatus = z.infer<typeof AnnouncementStatus>;

// ── isNew badge window — DERIVED at read, never a stored flag ───────────────────
// The "new" badge is computed from published_at + the site's new_tag_duration_*
// settings, so it decays with wall-clock time (a stored 0/1 would go stale). Unit →
// milliseconds; the window is `val * unitMs`. A bad/unknown unit degrades to days
// and a negative/NaN val to 0 (the store is validated on write, so this is only
// defense in depth). is_new_override (below) lets an editor force the badge.
export const DAY_MS = 86_400_000;
// The `Record<NewTagUnit, number>` type enforces a ms value for EVERY unit at compile
// time — a new NEW_TAG_UNITS member breaks the build here until it is given a window.
export const NEW_TAG_UNIT_MS: Record<NewTagUnit, number> = {
  days: DAY_MS,
  weeks: 7 * DAY_MS,
  months: 30 * DAY_MS,
};
export const newTagWindowMs = (val: number, unit: string): number => {
  const per = NEW_TAG_UNIT_MS[unit as NewTagUnit] ?? NEW_TAG_UNIT_MS.days;
  const v = Number.isFinite(val) && val >= 0 ? val : 0;
  return v * per;
};

// ── Attachments — a JSON list of labelled links (stored as attachments_json TEXT) ─
// Each entry is a display title + a URL + a coarse type (the MBSPSU attachments[]
// shape). The URL is NOT rendered as raw HTML — it rides an href on the website — so
// the body sanitizer never sees it, which is exactly why its SCHEME is judged here
// (schemas/href.ts HttpUrl, SEC-5): an absolute http(s) URL, never javascript:/data:.
// Bounded so a runaway list can never bloat the row.
export const AnnouncementAttachment = z.object({
  title: z.string().trim().min(1).max(300),
  url: HttpUrl,
  type: z.string().trim().min(1).max(50).default("pdf"),
  /** A short plain-text description beside the title (Django's document description, 0018 "Caps"). */
  description: z.string().trim().max(500).optional(),
});
export type AnnouncementAttachment = z.infer<typeof AnnouncementAttachment>;
export const ATTACHMENTS_MAX = 50;
/**
 * The attachment types the admin editor OFFERS — a suggested vocabulary, not a constraint
 * (`type` stays a free ≤50 string so an imported or API-written value survives a round trip).
 * Two of them are contracts the public site reads by name (nx-ssneri-web map.ts): `image`
 * is a news article's hero, `pdf` is a publication's download when pdf_media_id is null.
 */
export const ATTACHMENT_TYPES = ["pdf", "image", "doc", "link"] as const;

// ── The write-side field validators (one source, Create/Update both derive) ─────
/** Max body length — a generous bound that still keeps a runaway paste out of D1. */
export const BODY_HTML_MAX = 200_000;
/** Max summary length — ONE cap for every content type's summary (announcements / tenders / events / publications reuse it). 1000 → 2000 on 2026-09-13: a Django issue description runs 1267. */
export const SUMMARY_MAX = 2000;

const announcementCore = {
  type: AnnouncementType,
  title: z.string().trim().min(1).max(300),
  slug: z.string().trim().min(1).max(SLUG_MAX).regex(SLUG_RE, "Slug must be lower-kebab (a-z, 0-9, hyphen)"),
  /** RAW HTML — sanitized ONLY at the website render (HTMLRewriter), never here. Nullable to clear. */
  bodyHtml: z.string().max(BODY_HTML_MAX).nullable(),
  summary: z.string().trim().max(SUMMARY_MAX).nullable(),
  /** A route or a PDF URL the card links to — a relative path or http(s)/mailto/tel, never a script scheme (schemas/href.ts SafeHref). Nullable to clear. */
  link: SafeHref.nullable(),
  status: AnnouncementStatus,
  /** Explicit publish instant (epoch-ms). Usually left unset — the service stamps it when status→published. Nullable to clear. */
  publishedAt: EpochMs.nullable(),
  /** FK to a cms_category of type='announcement' — the service validates the cross-entity ref. Nullable to clear. */
  categoryId: EntityId.nullable(),
  attachments: z.array(AnnouncementAttachment).max(ATTACHMENTS_MAX).nullable(),
  /** Structured "Key Highlights" (0018, A3 — schemas/structured.ts Highlight, ≤30). Nullable to clear. */
  highlights: Highlights.nullable(),
  /** Bare-string tags (0018, A4 — the gallery rule: ≤60 chars, ≤50 per row). Nullable to clear. */
  tags: Tags.nullable(),
  /** Force the isNew badge on/off; null ⇒ derive from published_at + the setting. */
  isNewOverride: z.boolean().nullable(),
  /** Locale-ready seam — v1 writes only 'en' (db-cms 0004). */
  /** The row's locale — a canonical BCP 47 tag (./locale.ts, the locale contract). */
  locale: Locale,
};

/**
 * Create body — type + title required; slug OPTIONAL (the service derives it via
 * slugify(title) when absent, id-fallback on a non-sluggable title). status/locale
 * carry the DDL defaults; published_at is normally derived from the status.
 */
export const CreateCmsAnnouncement = z.object({
  type: announcementCore.type,
  title: announcementCore.title,
  slug: announcementCore.slug.optional(),
  bodyHtml: announcementCore.bodyHtml.optional(),
  summary: announcementCore.summary.optional(),
  link: announcementCore.link.optional(),
  status: announcementCore.status.default("draft"),
  publishedAt: announcementCore.publishedAt.optional(),
  categoryId: announcementCore.categoryId.optional(),
  attachments: announcementCore.attachments.optional(),
  highlights: announcementCore.highlights.optional(),
  tags: announcementCore.tags.optional(),
  isNewOverride: announcementCore.isNewOverride.optional(),
  locale: announcementCore.locale.default("en"),
});
export type CreateCmsAnnouncement = z.infer<typeof CreateCmsAnnouncement>;

/** Update body — a partial PATCH over the same fields (slug stays STABLE on rename; a slug collision still maps to 409). */
export const UpdateCmsAnnouncement = z.object(announcementCore).partial();
export type UpdateCmsAnnouncement = z.infer<typeof UpdateCmsAnnouncement>;

/** List query — keyset cursor+limit + optional ?type ?status ?categoryId ?locale filters (soft-deleted rows are always excluded). */
export const CmsAnnouncementListQuery = CursorQuery.extend({
  type: AnnouncementType.optional(),
  status: AnnouncementStatus.optional(),
  categoryId: EntityId.optional(),
  /** Exact-eq on the row's locale (0018) — the admin list narrows to one locale's rows; omitted lists every locale. */
  locale: Locale.optional(),
  /** Opt-in TRASH view — true lists soft-deleted rows only; omitted (the default) lists live rows. Coerced + optional so a stray value never 422s the query. */
  // Self-healing like the FE's ?trashed: only "1"/"true" opt into trash; anything else
  // (absent, "false", "0", garbage) heals to the live list — never flips to trash or 422s.
  trashed: z
    .enum(["1", "true"])
    .transform(() => true)
    .optional()
    .catch(undefined),
});
export type CmsAnnouncementListQuery = z.infer<typeof CmsAnnouncementListQuery>;

/**
 * The wire DTO — the read shape (camelCase; audit actor columns stay server-side).
 * `isNew` is DERIVED (not stored); `attachments` is the parsed list; `deletedAt`
 * surfaces so the admin trash view can tell live rows from trashed ones.
 */
export const CmsAnnouncement = z.object({
  id: EntityId,
  slug: z.string(),
  locale: z.string(),
  type: AnnouncementType,
  title: z.string(),
  bodyHtml: z.string().nullable(),
  summary: z.string().nullable(),
  link: z.string().nullable(),
  status: AnnouncementStatus,
  publishedAt: EpochMs.nullable(),
  categoryId: EntityId.nullable(),
  attachments: z.array(AnnouncementAttachment),
  /** 0018 — the parsed highlights list ([] when none). */
  highlights: z.array(Highlight),
  /** 0018 — the parsed tags list ([] when none). */
  tags: z.array(z.string()),
  isNewOverride: z.boolean().nullable(),
  isNew: z.boolean(),
  createdAt: EpochMs,
  updatedAt: EpochMs,
  deletedAt: EpochMs.nullable(),
});
export type CmsAnnouncement = z.infer<typeof CmsAnnouncement>;
